import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import {
  AssignmentStep,
  CourseNavigationContext,
} from '@/administration/pages/Course/MainLayoutView/atoms/NavigationActions/CourseNavigationProvider';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';
import { ASSIGNMENT_FILE_ERROR_MESSAGE } from '@/features/course/learner/constants/assignment';
import { useAssignmentSubmission } from '@/features/course/learner/hooks/useAssignmentSubmission';
import { CourseAssignmentSubmission } from '@/features/course/learner/molecules/CourseAssignmentSubmission';
import { AssignmentFile } from '@/features/course/learner/types/assignment';
import { ErrorCode, FileRejection } from '@/ui/DropArea';

const RootStyled = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: calc(100vh - 285px);
  padding: 0 28px;
  border-radius: 20px;
  background: ${(p) => p.theme.colors.primary.onMain};
  color: ${(p) => p.theme.colors.neutral.onContainer};
`;

const ContentStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 776px;
  word-wrap: break-word;
`;

const editorTextGutter = css`
  margin-left: 58px;
  margin-right: 58px;

  @media (min-width: ${(p) => p.theme.breakpoints.desktopLarge.end}px) {
    margin-left: 0;
    margin-right: 0;
  }
`;

const InstructionsStyled = styled.div`
  padding-bottom: 28px;
`;

const SubmissionStyled = styled.div`
  ${editorTextGutter};
`;

/** A picked file that is not submitted yet. It stays in the browser until Submit. */
type AssignmentDraft = {
  file: File;
  /** A blob URL of the picked file, so View opens it before anything reaches the server. */
  preview: AssignmentFile;
};

const getRejectionMessage = (rejections: FileRejection[]) => {
  const isTooLarge = rejections.some(({ errors }) => errors.some(({ code }) => code === ErrorCode.FileTooLarge));

  return isTooLarge ? ASSIGNMENT_FILE_ERROR_MESSAGE.tooLarge : ASSIGNMENT_FILE_ERROR_MESSAGE.notPdf;
};

type CourseAssignmentActivityProps = {
  /** The activity's server id — what submissions are stored against. */
  learningActivityId: number;
  /** The creator's content, shown above the submission. */
  instructions: ReactNode;
  onCompleted: VoidFunction;
};

/**
 * An assignment activity in the course flow: the creator's instructions and the learner's
 * PDF submission. The activity completes on Submit only — a picked file is not progress, and
 * it is not kept when the learner leaves the page.
 */
export const CourseAssignmentActivity = ({
  learningActivityId,
  instructions,
  onCompleted,
}: CourseAssignmentActivityProps): JSX.Element | null => {
  const { submission, loading, submitFile, submitting } = useAssignmentSubmission(learningActivityId);
  const { openSnackbar, closeSnackbar } = useContext(SharedSnackbarContext);
  const { setIsPageLoading, setAssignmentStep, assignmentStepActionRef } = useContext(CourseNavigationContext);

  const [draft, setDraft] = useState<AssignmentDraft | null>(null);
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);
  const dropAreaRef = useRef<HTMLButtonElement | null>(null);
  const highlightSubmitRef = useRef<VoidFunction | null>(null);
  const completionTriggeredRef = useRef(false);

  const isSubmitted = !!submission && !draft;
  const file = draft?.preview ?? submission?.file ?? null;
  const assignmentStep: AssignmentStep = isSubmitted ? null : draft ? 'submit' : 'upload';

  // The preview's blob keeps the whole file in memory until it is revoked
  useEffect(() => {
    if (!draft) return;

    return () => URL.revokeObjectURL(draft.preview.url);
  }, [draft]);

  useEffect(() => {
    setIsPageLoading(loading);
  }, [loading, setIsPageLoading]);

  // A submission loaded on return counts too, same as a passed quiz does.
  useEffect(() => {
    if (submission && !completionTriggeredRef.current) {
      completionTriggeredRef.current = true;
      onCompleted();
    }
  }, [submission, onCompleted]);

  useEffect(() => {
    setAssignmentStep(assignmentStep);

    if (assignmentStepActionRef) {
      assignmentStepActionRef.current =
        assignmentStep === 'upload'
          ? () => {
              dropAreaRef.current?.focus({ preventScroll: true });
              dropAreaRef.current?.click();
            }
          : () => highlightSubmitRef.current?.();
    }
  }, [assignmentStep, setAssignmentStep, assignmentStepActionRef]);

  // The navigation outlives the activity, so the next page must not inherit these.
  useEffect(
    () => () => {
      setAssignmentStep(null);

      if (assignmentStepActionRef) {
        assignmentStepActionRef.current = null;
      }
    },
    [setAssignmentStep, assignmentStepActionRef],
  );

  const showError = (message: string) => openSnackbar({ type: SnackbarType.DANGER, message });

  // A submitted file is only ever replaced by another submitted one, so replacing it uploads
  // straight away — the learner never drops back to a draft with Remove. Before the first
  // submit, a picked file stays in the browser until Submit.
  const handleFileSelected = async (selectedFile: File) => {
    closeSnackbar();

    if (!isSubmitted) {
      setDraft({ file: selectedFile, preview: { name: selectedFile.name, url: URL.createObjectURL(selectedFile) } });

      return;
    }

    setUploadingFileName(selectedFile.name);

    try {
      await submitFile(selectedFile);
    } catch {
      showError(ASSIGNMENT_FILE_ERROR_MESSAGE.uploadFailed);
    } finally {
      setUploadingFileName(null);
    }
  };

  const handleSubmit = async () => {
    if (!draft) return;

    try {
      await submitFile(draft.file);
      setDraft(null);
    } catch {
      showError(ASSIGNMENT_FILE_ERROR_MESSAGE.uploadFailed);
    }
  };

  // Same as the quiz: nothing until the submission is known, rather than an empty drop area
  // that turns into the submitted file a moment later.
  if (loading) return null;

  return (
    <RootStyled>
      <ContentStyled>
        <InstructionsStyled>{instructions}</InstructionsStyled>
        <SubmissionStyled>
          <CourseAssignmentSubmission
            file={file}
            submittedAt={isSubmitted ? submission?.submittedAt : undefined}
            uploadingFileName={uploadingFileName}
            submitting={submitting}
            onFileSelected={handleFileSelected}
            onFilesRejected={(rejections) => showError(getRejectionMessage(rejections))}
            onRemove={() => setDraft(null)}
            onSubmit={handleSubmit}
            dropAreaRef={dropAreaRef}
            setSubmitHighlightCallback={(highlight) => {
              highlightSubmitRef.current = highlight;
            }}
          />
        </SubmissionStyled>
      </ContentStyled>
    </RootStyled>
  );
};
